import { slackAppHost } from '@gonfalon/constants/constants';
// eslint-disable-next-line no-restricted-imports
import type { Map } from '@gonfalon/immutable-shim/immutableShim';
// oxlint-disable-next-line no-restricted-imports
import { fromJS } from '@gonfalon/immutable-shim/immutableShim';
import { createFormState } from '@gonfalon/legacy-forms/formUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { createSelector } from 'reselect';

import type { WebhookAction } from '#actions/webhooks';
import { forms as formActionTypes } from '#actionTypes/forms';
import registry from '#reducers/registry';
import type { Webhook, WebhookForm } from '#utils/webhookUtils';
import { createWebhookForm } from '#utils/webhookUtils';

const SLACK_APP_HOST = slackAppHost();

export const webhookFormKey = 'webhookForm';

const initialFormState = (webhook = createWebhookForm()) => createFormState(webhook);

export function webhookForm(state = initialFormState(), action: WebhookAction) {
  switch (action.type) {
    case 'webhooks/EDIT_WEBHOOK':
      return state.trackField(action.field).revalidate(action.webhook);
    case 'webhooks/CREATE_WEBHOOK':
    case 'webhooks/UPDATE_WEBHOOK':
      return state.submitting();
    case 'webhooks/CREATE_WEBHOOK_FAILED':
      return state.submitFailed(action.webhook.toForm(), action.error);
    case 'webhooks/UPDATE_WEBHOOK_FAILED':
      return state.submitFailed(state.modified, action.error);
    case 'webhooks/UPDATE_WEBHOOK_DONE':
      return state.submitted(action.webhook.toForm());
    case formActionTypes.BLUR:
      if (action.model !== webhookFormKey) {
        return state;
      }
      return state.handleBlur(action.field, state.modified);
    case formActionTypes.INITIALIZE:
      if (action.model !== webhookFormKey) {
        return state;
      }
      return initialFormState(action.initialState as WebhookForm);
    case formActionTypes.DESTROY:
      if (action.model !== webhookFormKey) {
        return state;
      }
      return initialFormState();
    case formActionTypes.DISABLE_SUBMIT:
      return state.disableSubmit();
    case formActionTypes.ENABLE_SUBMIT:
      return state.enableSubmit();
    default:
      return state;
  }
}

type WebhookState = ImmutableMap<{
  entities: Map<string, Webhook>;
  lastFetched: number;
  isFetching: boolean;
  error?: Error;
}>;

export function webhooks(
  state: WebhookState = fromJS({
    entities: {},
    lastFetched: null,
    isFetching: false,
  }),
  action: WebhookAction,
) {
  switch (action.type) {
    case 'webhooks/REQUEST_WEBHOOKS':
      return state.set('isFetching', true);
    case 'webhooks/REQUEST_WEBHOOKS_FAILED':
      return state.merge({
        isFetching: false,
        error: action.error,
      });
    case 'webhooks/REQUEST_WEBHOOKS_DONE':
      return state.merge({
        isFetching: false,
        lastFetched: action.timestamp,
        entities: action.response.getIn(['entities', 'webhooks']),
      });
    case 'webhooks/CREATE_WEBHOOK_DONE':
    case 'webhooks/UPDATE_WEBHOOK_DONE':
      return state.update('entities', (entities) => entities.set(action.webhook._id, action.webhook));
    case 'webhooks/DELETE_WEBHOOK_DONE':
      return state.update('entities', (entities) => entities.delete(action.webhook._id));
    default:
      return state;
  }
}

import type { GlobalState } from '#reducers/state';
export const webhooksSelector = (state: GlobalState): WebhookState => state.webhooks;

export const getSlackAppWebhooks = (webhookState: WebhookState) =>
  webhookState.get('entities').filter((webhook) => !!SLACK_APP_HOST && webhook.url.startsWith(SLACK_APP_HOST));

export const webhookByIdSelector = createSelector(
  webhooksSelector,
  (_: GlobalState, props: { match: { params: { webhookId: string } } }) => props.match.params.webhookId,
  (wh, webhookId) =>
    fromJS({
      lastFetched: wh.get('lastFetched'),
      isFetching: wh.get('isFetching'),
      entity: wh.getIn(['entities', webhookId]),
    }),
);

export const webhookFormSelector = (state: GlobalState) => state.webhookForm;

registry.addReducers({ webhookForm, webhooks });

declare module '#reducers/state' {
  interface GlobalState {
    webhooks: ReturnType<typeof webhooks>;
    webhookForm: ReturnType<typeof webhookForm>;
  }
}
